import type { ArdoqId } from '@ardoq/api-types';

/**
 * How a node earns its area. `count` is what the view falls back to when no field is chosen:
 * every leaf weighs one, so the picture is the shape of the hierarchy. Summing nothing instead
 * gives every node an area of zero, which d3 lays out as a blank frame — indistinguishable from
 * a traversal that returned nothing.
 */
export type TreemapSizing =
  | { kind: 'count' }
  | { kind: 'field'; fieldName: string };

/**
 * Checked against the fields the view is offering, not just for emptiness.
 *
 * `sizeByFieldName` is persisted and belongs to the view rather than to a workspace, so it
 * outlives the field it names: the user changes workspace, the field is deleted or renamed, the
 * traversal is edited until the field's component type leaves scope, or a viewpoint arrives
 * carrying a confidential field its reader cannot see — which is in the offered list rather than
 * absent from it, the dropdown disabling such an option rather than dropping it, so the flag has
 * to be read as well as the name. Nothing in the settings notices. Sized by
 * a name no component answers to, every node weighs nothing and all three views are the blank
 * frame above — with the panel naming a field that is not in its own dropdown.
 *
 * Emptiness is one member of that set: turning a field dropdown off persists `''`, not `null`.
 */
export const toSizing = (
  sizeByFieldName: string | null,
  offeredFields: { name: string; isConfidential: boolean }[]
): TreemapSizing =>
  sizeByFieldName &&
  offeredFields.some(
    ({ name, isConfidential }) => name === sizeByFieldName && !isConfidential
  )
    ? { kind: 'field', fieldName: sizeByFieldName }
    : { kind: 'count' };

/**
 * A component's value for a field, or `null` where it carries none. The two modes read from
 * different places — the streamed component collection in workspace mode, the viewpoint's own
 * scope data in viewpoint mode — and nothing else about building the hierarchy differs.
 */
export type ReadFieldValue = (
  modelId: ArdoqId,
  fieldName: string
) => number | null;
